/**
 * Tenant Registry
 *
 * Central source of truth for all white-label tenant configurations and their
 * branding assets (logos and icons).
 *
 * Component fallback chain:
 *  - TenantLogo  : tenant logo  → ButterLogo variant="full"
 *  - TenantIcon  : tenant icon  → ButterLogo variant="icon"
 *  - TenantBrand : tenant icon  → tenant logo → ButterLogo variant="full"
 *                  (pass `preferLogo` to skip the icon layer)
 *
 * Adding a new tenant:
 *  1. Add the logo SVG under ./logos/ (thread a `viewBox` prop so its crop is
 *     tunable) and register it in TENANT_LOGO_MAP.
 *  2. Add an entry to TENANTS with at least `handle`, and set `logoSize`
 *     ('wide' wordmark vs 'square' badge — drives the sizing height).
 *  3. Preview in the Storybook `Brand/Playground` story. If the artwork sits in
 *     whitespace, dial in `logoViewBox` (crop); if it's a dark full-bleed plate,
 *     set `roundedLogo`. Copy the generated snippet back into the entry.
 *  4. Optionally add the icon SVG under ./icons/ and register it in TENANT_ICON_MAP
 *     (most clients won't have a standalone icon — that's fine, TenantIcon and
 *     TenantBrand will fall back to the Butter icon automatically).
 *  5. Only if the tenant is going on the dark canvas: register a dark-ground
 *     mark in TENANT_DARK_LOGO_MAP if their regular one vanishes on black, tune
 *     the `dark` block in the Playground, and set `brandedCanvas: true`.
 *
 * Colors are NOT configured here. A tenant's palette is `tenants.colorTheme` →
 * the theme registry (`packages/ui/src/styles/themes.ts`); read `theme-*` for
 * actual colors. This file once carried a `primaryColor` mirror of each theme's
 * 500, which nothing read and which silently drifted — it has been removed
 * rather than re-synced. See `packages/ui/docs/ColorTheming.mdx`.
 *
 * See README.md for the full sizing / cropping / tuning reference.
 */

import type { ReactElement } from 'react';

// ---------------------------------------------------------------------------
// Logo / icon imports (keep at top so the maps below are easy to audit)
// ---------------------------------------------------------------------------

import { SweetScoreLogo } from './logos/sweet-score';
import { SpritzLogo } from './logos/spritz';
import { DrivingRangeCollectiveLogo } from './logos/driving-range-collective';
import { LinksIndoorLogo } from './logos/links-indoor';
import { TheGreenRoomIGCLogo } from './logos/the-green-room-igc';
import { GreenboxIndoorGolfLogo } from './logos/greenbox-indoor-golf';
import { TapAndTeesLogo } from './logos/tap-and-tees';
import { VirtualClubhouseGolfLogo } from './logos/virtualclubhousegolf';
import { VirtualClubhouseGolfDarkLogo } from './logos/virtualclubhousegolf-dark';
import { HiveGolfLogo } from './logos/hivegolf';
import { TheLinksClubLogo } from './logos/the-links-club';
import { SwingshiftLogo } from './logos/swingshift';
import { SLGolfLoungeLogo } from './logos/slgolflounge';
import { Swing365Logo } from './logos/swing365';
import { Swing365DarkLogo } from './logos/swing365-dark';
import { WestTexasGolfLogo } from './logos/westtexasgolf';
import { NextLevelGolfLogo } from './logos/nextlevelgolf';
import { PerfectParGolfStudioLogo } from './logos/perfectpargolfstudio';
import { Fairway365Logo } from './logos/fairway365';
import { DraperIndoorGolfLogo } from './logos/draperindoorgolf';
import { DraperIndoorGolfDarkLogo } from './logos/draperindoorgolf-dark';
import { GreensideGolfClubLogo } from './logos/greensidegolfclub';
import { GreensideGolfClubDarkLogo } from './logos/greensidegolfclub-dark';
import { SbGolfClubLogo } from './logos/sbgolfclub';
import { SbGolfClubDarkLogo } from './logos/sbgolfclub-dark';
import { SimSuiteGolfLogo } from './logos/simsuitegolf';
import { SimSuiteGolfDarkLogo } from './logos/simsuitegolf-dark';
import { GolfersLoungeLogo } from './logos/golferslounge';
import { GolfersLoungeDarkLogo } from './logos/golferslounge-dark';
import { BirdiesBayGolfLogo } from './logos/birdiesbaygolf';
import { ThreePGolfSimsLogo } from './logos/3pgolfsims';
import { ThreePGolfSimsDarkLogo } from './logos/3pgolfsims-dark';
import { ValleyGolfSimulatorsLogo } from './logos/valleygolfsimulators';
import { ValleyGolfSimulatorsDarkLogo } from './logos/valleygolfsimulators-dark';
import { SmashFactor150Logo } from './logos/smashfactor-150';
import { SmashFactor150DarkLogo } from './logos/smashfactor-150-dark';
import { IndoorGolfClubLogo } from './logos/indoorgolfclub';
import { IndoorGolfClubDarkLogo } from './logos/indoorgolfclub-dark';
import { GolfIronEagleLogo } from './logos/golfironeagle';
import { GolfIronEagleDarkLogo } from './logos/golfironeagle-dark';
import { CaseysIndoorGolfClubLogo } from './logos/caseysindoorgolfclub';
import { GManGolfSimLogo } from './logos/gmangolfsim';
import { GolfLabLogo } from './logos/thegolflabcf';
import { GolfLabDarkLogo } from './logos/thegolflabcf-dark';
import { SamsGolfLogo } from './logos/samsgolf';
import { SamsGolfDarkLogo } from './logos/samsgolf-dark';
import { SwingPathGolfClubLogo } from './logos/swingpathgolfclub';
import { SwingPathGolfClubDarkLogo } from './logos/swingpathgolfclub-dark';
import { Swing18TabLogo } from './logos/swing18-tab';
import { Swing18TabDarkLogo } from './logos/swing18-tab-dark';
import { MeridianaGolfSwingLogo } from './logos/meridianagolfswing';
import { MeridianaGolfSwingDarkLogo } from './logos/meridianagolfswing-dark';
import { TeeUpIndoorGolfLogo } from './logos/teeupindoorgolf';
import { TeeUpIndoorGolfDarkLogo } from './logos/teeupindoorgolf-dark';
import { BunkersWvLogo } from './logos/bunkerswv';
import { BunkersWvDarkLogo } from './logos/bunkerswv-dark';
import { MettleLogo } from './logos/mettle';
import { MettleDarkLogo } from './logos/mettle-dark';
import { TeeToGreenLogo } from './logos/teetogreen';
import { TeeToGreenDarkLogo } from './logos/teetogreen-dark';
import { CjsSwingStudioLogo } from './logos/cjsswingstudio';
import { CjsSwingStudioDarkLogo } from './logos/cjsswingstudio-dark';
import { FourePlayGolfLogo } from './logos/4oreplaygolf';
import { GolfBarnLogo } from './logos/thegolf-barn';
import { GolfBarnDarkLogo } from './logos/thegolf-barn-dark';
import { GreenJacketGolfLogo } from './logos/thegreenjacketgolf';
import { GreenJacketGolfDarkLogo } from './logos/thegreenjacketgolf-dark';

import { DrivingRangeCollectiveIcon } from './icons/driving-range-collective';
import { LinksIndoorIcon } from './icons/links-indoor';
import { SwingPathGolfClubIcon } from './icons/swingpathgolfclub';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

/**
 * Per-surface presentation overrides.
 *
 * Only `dark` exists today, because there is exactly one dark surface
 * (`BrandCanvas`, behind `brandedCanvas`). Light surfaces read the flat fields
 * on `TenantConfig` — the headers work and are deliberately untouched.
 *
 * Every field is optional and overrides the flat/base value for that surface
 * only, so a tenant declares just the deltas. A crop tuned for a 112px mark on
 * black is usually wrong for a 36px mark in a white header; this is where that
 * difference lives.
 */
export interface TenantSurfaceConfig {
  /** Crop override for this surface. Falls back to the base `logoViewBox`. */
  viewBox?: string;
  /** Rounding override for this surface. Falls back to the base `roundedLogo`. */
  rounded?: boolean;
  /**
   * Multiplier on the size tier's height (and its max-width cap). `1` is the
   * shipped tier; `1.15` renders 15% larger. A number, never a Tailwind class —
   * that is what lets the Storybook control be a slider and keeps this whole
   * object serializable.
   */
  scale?: number;
}

/**
 * A tenant's branding configuration.
 *
 * **Every field must stay JSON-serializable** — no component references, no
 * Tailwind class strings. Assets live in the code maps below and are keyed by
 * handle; this object is data, so it can move to a DB table later without
 * changing shape. That is the whole of the "DB-friendly later" requirement.
 */
export interface TenantConfig {
  handle: string;
  /**
   * Aspect-ratio hint for logo sizing.
   * - 'wide'   — horizontal wordmark (ratio > ~2:1). Sized by height, capped by max-width.
   * - 'square' — badge / crest / icon (ratio ~1:1). Sized taller to match visual weight of wide logos.
   * Defaults to 'wide' when omitted.
   */
  logoSize?: 'wide' | 'square';
  /**
   * Rounds the corners of a logo that paints a full-bleed background (e.g. VirtualClubhouse's
   * solid black plate). Only honored by logo components that render such a background — a no-op
   * for transparent wordmarks. Off by default.
   */
  roundedLogo?: boolean;
  /**
   * Crops the source SVG to its artwork by overriding the viewBox, removing baked-in
   * whitespace/padding around a mark on an oversized (often square) canvas. Format:
   * `"minX minY width height"`. Only honored by SVG logo components authored to accept
   * a `viewBox` prop (see "Adding a new tenant" above).
   *
   * Don't hand-guess these — tune them live in Storybook's **Brand/Playground**,
   * which shows every surface the mark renders on at once, then paste the emitted
   * snippet here. A crop that reads on the dark canvas can easily be wrong in a
   * 36px header, which is the whole reason that story shows both.
   */
  logoViewBox?: string;
  /**
   * Opts the tenant into the branded dark-canvas surfaces (the /welcome landing
   * and the OTA overlay's canvas variant in mobile-app). Whitelist, not default:
   * the canvas needs a logo asset that reads well on black, and most current
   * club logos don't. Tenants without the flag keep the classic light flows.
   *
   * Migration runbook (asset swap → whitelist → eventual flag removal):
   * `apps/mobile-app/docs/branded-canvas-whitelist.md`.
   *
   * Deliberately NOT derived from `TENANT_DARK_LOGO_MAP` membership. The two
   * look collapsible but aren't: `virtualclubhousegolf` is canvas-ready with no
   * dark entry, because its regular logo is a black plate that already reads on
   * black. This flag is a human judgment; the map is an asset fact.
   */
  brandedCanvas?: boolean;
  /**
   * Presentation overrides for the dark canvas. Omit unless the tenant's mark
   * needs different treatment there than on light chrome.
   */
  dark?: TenantSurfaceConfig;
}

export interface LogoProps {
  width?: number | string;
  height?: number | string;
  className?: string;
  /** When true the SVG is hidden from assistive technology (decorative use). Defaults to true. */
  ariaHidden?: boolean;
  /**
   * Rounds the corners of a full-bleed background plate, when the logo has one.
   * Ignored by logos without a solid background. Defaults to false.
   */
  rounded?: boolean;
  /**
   * Overrides the SVG viewBox to crop to the artwork. Defaults to the logo's own viewBox.
   * SVG logo components should thread this through to their `<svg viewBox={viewBox}>` so
   * the crop is tunable per tenant via the registry's `logoViewBox`.
   */
  viewBox?: string;
}

export type LogoComponent = (props: LogoProps) => ReactElement;

// ---------------------------------------------------------------------------
// Tenant config map
// ---------------------------------------------------------------------------

export const TENANTS: Record<string, TenantConfig> = {
  /** Reserved for internal demos — not a white-label client. No logo registered; always falls back to ButterLogo. */
  generic: {
    handle: 'generic',
    logoSize: 'wide',        // 413×87 (generic.tsx exists but is not in TENANT_LOGO_MAP — intentional)
    brandedCanvas: true,
  },
  'sweet-score': {
    handle: 'sweet-score',
    logoSize: 'wide',                 // wordmark (~2.6:1) floating in a 2250² canvas
    logoViewBox: '0 660 2250 865',    // crop out the vertical whitespace
  },
  'spritz-golf': {
    handle: 'spritz-golf',
    logoSize: 'square',      // 665×516 — near-square, treated as square
  },
  /** Staging / QA tenant — not a production white-label client */
  'drivingrangecollective': {
    handle: 'drivingrangecollective',
    logoSize: 'square',                // crest, ~1:1
    logoViewBox: '150 125 1240 1290',  // crop the padding around the crest
    roundedLogo: true,                 // white crest tile — soften the corners
  },
  'linksindoor': {
    handle: 'linksindoor',
    logoSize: 'wide',        // 184×78
  },
  /** Handle ≠ theme name — this tenant renders with the `greenroom` palette. */
  'thegreenroomigc': {
    handle: 'thegreenroomigc',
    logoSize: 'wide',         // horizontal lockup (~3.1:1); crop baked into the imageLogo asset
  },
  'greenboxindoorgolf': {
    handle: 'greenboxindoorgolf',
    logoSize: 'wide',         // 'GREENBOX' wordmark (~2.3:1); crop baked into the imageLogo asset
  },
  'tapandtees': {
    handle: 'tapandtees',
    logoSize: 'square',       // 1024×1024 — square
  },
  'virtualclubhousegolf': {
    handle: 'virtualclubhousegolf',
    logoSize: 'square',      // 649.36×513.02 — near-square, treated as square
    roundedLogo: true,       // solid black plate — soften the corners
    brandedCanvas: true,     // the dark welcome/OTA canvas was designed for VC
  },
  'hivegolf': {
    handle: 'hivegolf',
    logoSize: 'square',      // 1024×1024
  },
  'thelinksclub': {
    handle: 'thelinksclub',
    logoSize: 'square',       // 1024×1024
  },
  'swingshift': {
    handle: 'swingshift',
    logoSize: 'square',       // 1024×1024
  },
  'slgolflounge': {
    handle: 'slgolflounge',
    logoSize: 'square',       // 1024×1024
  },
  'swing365': {
    handle: 'swing365',
    logoSize: 'wide',         // raster wordmark, trimmed to 918×217 (~4.23:1)
    brandedCanvas: true,
  },
  'westtexasgolf': {
    handle: 'westtexasgolf',
    logoSize: 'square',       // 1024×1024
  },
  'nextlevelgolf': {
    handle: 'nextlevelgolf',
    logoSize: 'square',       // 1024×1024
  },
  'perfectpargolfstudio': {
    handle: 'perfectpargolfstudio',
    logoSize: 'square',       // 1024×1024
  },
  'fairway365': {
    handle: 'fairway365',
    logoSize: 'wide',         // raster wordmark, 1000×334 (~3:1)
    roundedLogo: true,        // solid grey plate — soften the corners
  },
  'draperindoorgolf': {
    handle: 'draperindoorgolf',
    logoSize: 'square',       // 1024×1024 — square badge
    brandedCanvas: true,      // dark variant supplied (green icon on black plate)
  },
  'greensidegolfclub': {
    handle: 'greensidegolfclub',
    logoSize: 'wide',         // raster wordmark, ~3.2:1
    brandedCanvas: true,      // dark variant supplied
  },
  'sbgolfclub': {
    handle: 'sbgolfclub',
    logoSize: 'square',       // 120×120 crest
    brandedCanvas: true,      // dark variant supplied
  },
  'simsuitegolf': {
    handle: 'simsuitegolf',
    logoSize: 'square',       // 240×200 lockup (~1.2:1), treated as square
    brandedCanvas: true,      // dark variant supplied
  },
  'golferslounge': {
    handle: 'golferslounge',
    logoSize: 'square',       // 120×120 crest + wordmark lockup
    brandedCanvas: true,      // dark variant supplied
  },
  'birdiesbaygolf': {
    handle: 'birdiesbaygolf',
    logoSize: 'square',       // 140×140 mark
    brandedCanvas: true,      // single teal mark reads on both light and dark grounds — client's "dark" file was byte-identical to the regular one
  },
  '3pgolfsims': {
    handle: '3pgolfsims',
    logoSize: 'square',       // 207×120 mark-over-wordmark lockup (~1.7:1), treated as square
    brandedCanvas: true,      // dark variant supplied
  },
  'valleygolfsimulators': {
    handle: 'valleygolfsimulators',
    logoSize: 'square',       // 120×120 circular crest
    brandedCanvas: true,      // dark variant supplied; glow lifted to 300 (see glow.ts)
  },
  'smashfactor-150': {
    handle: 'smashfactor-150',
    logoSize: 'square',       // 120×120 golfer-over-ball mark with script wordmark
    brandedCanvas: true,      // dark variant supplied
  },
  'indoorgolfclub': {
    handle: 'indoorgolfclub',
    logoSize: 'square',       // 58×120 portrait door mark over "IGC"
    brandedCanvas: true,      // dark variant supplied (recoloured from the single-ink mark — designer to confirm)
  },
  'golfironeagle': {
    handle: 'golfironeagle',
    logoSize: 'square',       // 102×120 eagle-and-iron badge, grayscale; dark variant is the designer's own
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'caseysindoorgolfclub': {
    handle: 'caseysindoorgolfclub',
    logoSize: 'square',       // 140×104 round badge + ribbon (no tagline)
    brandedCanvas: true,      // same artwork serves the dark map; glow lifted to 400 (see glow.ts)
  },
  'gmangolfsim': {
    handle: 'gmangolfsim',
    logoSize: 'square',       // 189×100 G-MAN wordmark + script tagline (~1.9:1); 'wide' renders it too small on the dark canvas
    brandedCanvas: true,      // same artwork serves the dark map; glow lifted to 400 (see glow.ts)
  },
  'thegolflabcf': {
    handle: 'thegolflabcf',
    logoSize: 'square',       // 208×120 flask monogram + script wordmark (~1.7:1), treated as square
    brandedCanvas: true,      // dark variant supplied
  },
  'samsgolf': {
    handle: 'samsgolf',
    logoSize: 'square',       // 119×120 round badge
    brandedCanvas: true,      // dark variant supplied
  },
  'swingpathgolfclub': {
    handle: 'swingpathgolfclub',
    logoSize: 'wide',         // 299×80 SwingPath wordmark (~3.7:1); dark variant is the designer's own Reversed
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'swing18-tab': {
    handle: 'swing18-tab',
    logoSize: 'square',       // 218×120 Swing18 lockup with flag (~1.8:1), generated svg without number
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'meridianagolfswing': {
    handle: 'meridianagolfswing',
    logoSize: 'square',       // 121×120 round badge: black on light (Figma 13767:587964), gold on dark
    brandedCanvas: true,      // dark variant supplied (the gold badge, ~10:1 on black)
  },
  'teeupindoorgolf': {
    handle: 'teeupindoorgolf',
    logoSize: 'square',       // 120×120 round house-and-tee mark; dark variant is the designer's own
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'bunkerswv': {
    handle: 'bunkerswv',
    logoSize: 'wide',         // 513×240 B + BUNKERS wordmark (~2.1:1); PNG via imageLogo; dark PNG has a #000 plate that blends with the canvas
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'mettle': {
    handle: 'mettle',
    logoSize: 'square',       // 480×356 shield + METTLE GOLF lockup (~1.35:1); PNG via imageLogo; dark PNG has a #000 plate that blends with the canvas
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'teetogreen': {
    handle: 'teetogreen',
    logoSize: 'square',       // 245×240 golf-ball mark + script wordmark; PNG via imageLogo; dark PNG has a #000 plate that blends with the canvas
    brandedCanvas: true,      // dark variant supplied; glow lifted to 400 (see glow.ts)
  },
  'cjsswingstudio': {
    handle: 'cjsswingstudio',
    logoSize: 'square',       // 98×82 CJ's wordmark + golfer (~1.2:1); white-on-transparent dark variant is the designer's own
    brandedCanvas: true,      // dark variant supplied
  },
  '4oreplaygolf': {
    handle: '4oreplaygolf',
    logoSize: 'square',       // 552×301 green banner (~1.8:1) with gold lettering; PNG via imageLogo; full-bleed plate, no dark asset
    roundedLogo: true,        // solid green plate — soften the corners
  },
  'thegolf-barn': {
    handle: 'thegolf-barn',
    logoSize: 'wide',       // italic wordmark ~3.4:1 (light) / 3:1 (dark, own black plate); designer dark variant supplied; no dark glow lift yet
    brandedCanvas: true,      // dark variant supplied
  },
  'thegreenjacketgolf': {
    handle: 'thegreenjacketgolf',
    logoSize: 'square',       // 617×404 badge + wordmark (~1.5:1) PNG via imageLogo; designer's white dark variant (617×569)
    brandedCanvas: true,      // dark variant supplied
  },
};

export function getTenantConfig(handle?: string): TenantConfig {
  if (!handle || !Object.prototype.hasOwnProperty.call(TENANTS, handle)) {
    return TENANTS.generic;
  }
  return TENANTS[handle];
}

// ---------------------------------------------------------------------------
// Logo map  (full-width horizontal SVGs)
// Fallback: ButterLogo variant="full"
// ---------------------------------------------------------------------------

export const TENANT_LOGO_MAP: Record<string, LogoComponent> = {
  'sweet-score': SweetScoreLogo,
  'spritz-golf': SpritzLogo,
  'drivingrangecollective': DrivingRangeCollectiveLogo,
  'linksindoor': LinksIndoorLogo,
  'thegreenroomigc': TheGreenRoomIGCLogo,
  'greenboxindoorgolf': GreenboxIndoorGolfLogo,
  'tapandtees': TapAndTeesLogo,
  'virtualclubhousegolf': VirtualClubhouseGolfLogo,
  'hivegolf': HiveGolfLogo,
  'thelinksclub': TheLinksClubLogo,
  'swingshift': SwingshiftLogo,
  'slgolflounge': SLGolfLoungeLogo,
  'swing365': Swing365Logo,
  'westtexasgolf': WestTexasGolfLogo,
  'nextlevelgolf': NextLevelGolfLogo,
  'perfectpargolfstudio': PerfectParGolfStudioLogo,
  'fairway365': Fairway365Logo,
  'draperindoorgolf': DraperIndoorGolfLogo,
  'greensidegolfclub': GreensideGolfClubLogo,
  'sbgolfclub': SbGolfClubLogo,
  'simsuitegolf': SimSuiteGolfLogo,
  'golferslounge': GolfersLoungeLogo,
  'birdiesbaygolf': BirdiesBayGolfLogo,
  '3pgolfsims': ThreePGolfSimsLogo,
  'valleygolfsimulators': ValleyGolfSimulatorsLogo,
  'smashfactor-150': SmashFactor150Logo,
  'indoorgolfclub': IndoorGolfClubLogo,
  'golfironeagle': GolfIronEagleLogo,
  'caseysindoorgolfclub': CaseysIndoorGolfClubLogo,
  'gmangolfsim': GManGolfSimLogo,
  'thegolflabcf': GolfLabLogo,
  'samsgolf': SamsGolfLogo,
  'swingpathgolfclub': SwingPathGolfClubLogo,
  'swing18-tab': Swing18TabLogo,
  'meridianagolfswing': MeridianaGolfSwingLogo,
  'teeupindoorgolf': TeeUpIndoorGolfLogo,
  'bunkerswv': BunkersWvLogo,
  'mettle': MettleLogo,
  'teetogreen': TeeToGreenLogo,
  'cjsswingstudio': CjsSwingStudioLogo,
  '4oreplaygolf': FourePlayGolfLogo,
  'thegolf-barn': GolfBarnLogo,
  'thegreenjacketgolf': GreenJacketGolfLogo,
};

// ---------------------------------------------------------------------------
// Dark-surface logo map  (marks authored for dark grounds — the branded canvas)
//
// Optional per-tenant override, resolved only when a call site passes
// `surface="dark"` (BrandCanvas does). A tenant without an entry renders its
// regular logo on dark surfaces — correct for plate logos and colored badges,
// wrong only for marks that vanish on black, which is exactly when a client
// supplies a dark variant. Entries are usually `imageLogo(...)` raster assets:
// dark variants are re-exports of print/apparel artwork, and clients rarely
// have them as SVG.
// ---------------------------------------------------------------------------

export const TENANT_DARK_LOGO_MAP: Record<string, LogoComponent> = {
  'virtualclubhousegolf': VirtualClubhouseGolfDarkLogo,
  'swing365': Swing365DarkLogo,
  'draperindoorgolf': DraperIndoorGolfDarkLogo,
  'greensidegolfclub': GreensideGolfClubDarkLogo,
  'sbgolfclub': SbGolfClubDarkLogo,
  'simsuitegolf': SimSuiteGolfDarkLogo,
  'golferslounge': GolfersLoungeDarkLogo,
  '3pgolfsims': ThreePGolfSimsDarkLogo,
  'valleygolfsimulators': ValleyGolfSimulatorsDarkLogo,
  'smashfactor-150': SmashFactor150DarkLogo,
  'indoorgolfclub': IndoorGolfClubDarkLogo,
  'golfironeagle': GolfIronEagleDarkLogo,
  'caseysindoorgolfclub': CaseysIndoorGolfClubLogo, // same artwork as light; the gradient badge reads on black
  'gmangolfsim': GManGolfSimLogo, // same artwork as light; the white keyline carries it on black
  'thegolflabcf': GolfLabDarkLogo,
  'samsgolf': SamsGolfDarkLogo,
  'swingpathgolfclub': SwingPathGolfClubDarkLogo,
  'swing18-tab': Swing18TabDarkLogo,
  'meridianagolfswing': MeridianaGolfSwingDarkLogo,
  'teeupindoorgolf': TeeUpIndoorGolfDarkLogo,
  'bunkerswv': BunkersWvDarkLogo,
  'mettle': MettleDarkLogo,
  'teetogreen': TeeToGreenDarkLogo,
  'cjsswingstudio': CjsSwingStudioDarkLogo,
  'thegolf-barn': GolfBarnDarkLogo,
  'thegreenjacketgolf': GreenJacketGolfDarkLogo,
};

// ---------------------------------------------------------------------------
// Icon map  (square / compact mark SVGs)
// Fallback: ButterLogo variant="icon"
//
// Most white-label clients won't supply a standalone icon — leave them out
// here and the components will fall back to the Butter icon gracefully.
// When a client does supply one, add it below:
//
//   import { SweetScoreIcon } from './icons/sweet-score';
//   'sweet-score': SweetScoreIcon,
// ---------------------------------------------------------------------------

export const TENANT_ICON_MAP: Record<string, LogoComponent> = {
  // 'sweet-score': SweetScoreIcon,
  // 'spritz-golf': SpritzIcon,
  'drivingrangecollective': DrivingRangeCollectiveIcon,
  'linksindoor': LinksIndoorIcon,
  // The Links Club's brand mark is a square badge — reuse it as the icon.
  'thelinksclub': TheLinksClubLogo,
  // SGC shield. Light surfaces only — there is no dark icon map; the dark canvas uses the dark logo.
  'swingpathgolfclub': SwingPathGolfClubIcon,
};
